import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { probeModel } from '../utils/anthropicClient';

const { FiZap, FiCheckCircle, FiXCircle, FiLoader, FiAlertTriangle } = FiIcons;

/**
 * ALL known Anthropic model IDs to probe.
 * Sorted newest → oldest so working models appear first.
 */
const PROBE_MODELS = [
  'claude-opus-4-5',
  'claude-sonnet-4-5',
  'claude-haiku-4-5',
  'claude-opus-4-0',
  'claude-sonnet-4-0',
  'claude-3-7-sonnet-20250219',
  'claude-3-5-sonnet-20241022',
  'claude-3-5-sonnet-20240620',
  'claude-3-5-haiku-20241022',
  'claude-3-opus-20240229',
  'claude-3-sonnet-20240229',
  'claude-3-haiku-20240307',
  'claude-2.1',
  'claude-2.0',
  'claude-instant-1.2',
];

const StatusIcon = ({ status }) => {
  if (status === 'pending') return <SafeIcon icon={FiLoader} className="text-slate-500 animate-spin text-sm" />;
  if (status === 'ok') return <SafeIcon icon={FiCheckCircle} className="text-green-400 text-sm" />;
  if (status === 'rate_limited') return <SafeIcon icon={FiAlertTriangle} className="text-yellow-400 text-sm" />;
  if (status === 'error') return <SafeIcon icon={FiXCircle} className="text-red-400 text-sm" />;
  return <div className="w-3 h-3 rounded-full bg-slate-700" />;
};

const ModelTester = ({ apiKey, proxyUrl, onSelectModel }) => {
  const [results, setResults] = useState({});
  const [testing, setTesting] = useState(false);

  const runTests = async () => {
    if (!apiKey || !apiKey.trim().startsWith('sk-ant-')) {
      alert('Please enter a valid Anthropic API key first (must start with sk-ant-)');
      return;
    }
    setTesting(true);
    setResults({});

    for (const modelId of PROBE_MODELS) {
      setResults((prev) => ({ ...prev, [modelId]: { status: 'pending', error: null } }));
      const { ok, error } = await probeModel(apiKey, modelId, proxyUrl || null);
      const isRateLimit = error && error.includes('429');
      setResults((prev) => ({
        ...prev,
        [modelId]: {
          status: ok ? 'ok' : isRateLimit ? 'rate_limited' : 'error',
          error,
        },
      }));
      await new Promise((r) => setTimeout(r, 300));
    }
    setTesting(false);
  };

  const workingModels = PROBE_MODELS.filter(
    (m) => results[m]?.status === 'ok' || results[m]?.status === 'rate_limited'
  );

  return (
    <div className="p-4 bg-slate-950/60 rounded-lg border border-slate-800 space-y-3">
      <div className="flex items-center gap-2">
        <SafeIcon icon={FiZap} className="text-yellow-400 text-sm" />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Model Access Tester
        </span>
        <span className="ml-auto text-xs text-slate-600 italic">finds which models work for your key</span>
      </div>

      <p className="text-xs text-slate-500">
        Getting 404 errors? Click below to probe all known Anthropic model IDs against your API key.
        Green = accessible, Red = not available on your tier.
      </p>

      <button
        type="button"
        onClick={runTests}
        disabled={testing}
        className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold border transition-all ${
          testing
            ? 'border-slate-700 text-slate-500 cursor-not-allowed'
            : 'border-yellow-600/50 text-yellow-400 hover:bg-yellow-950/30'
        }`}
      >
        <SafeIcon icon={FiZap} className={testing ? 'animate-pulse' : ''} />
        {testing ? 'Testing models...' : 'Test All Models Against My API Key'}
      </button>

      {Object.keys(results).length > 0 && (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {PROBE_MODELS.map((modelId) => {
            const r = results[modelId];
            if (!r) return null;
            return (
              <div
                key={modelId}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-all ${
                  r.status === 'ok'
                    ? 'bg-green-950/20 border-green-800/30'
                    : r.status === 'rate_limited'
                    ? 'bg-yellow-950/20 border-yellow-800/30'
                    : r.status === 'pending'
                    ? 'bg-slate-800/40 border-slate-700/40'
                    : 'bg-red-950/10 border-red-900/20'
                }`}
              >
                <StatusIcon status={r.status} />
                <span className={`font-mono flex-1 ${r.status === 'ok' || r.status === 'rate_limited' ? 'text-white' : 'text-slate-500'}`}>
                  {modelId}
                </span>
                {(r.status === 'ok' || r.status === 'rate_limited') && (
                  <button
                    type="button"
                    onClick={() => onSelectModel(modelId)}
                    className="text-xs px-2 py-0.5 bg-green-800/40 text-green-300 rounded hover:bg-green-700/40 transition-colors font-semibold"
                  >
                    Use This
                  </button>
                )}
                {r.error && r.status !== 'ok' && (
                  <span className="text-slate-600 truncate max-w-[160px]" title={r.error}>{r.error}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!testing && workingModels.length > 0 && (
        <div className="p-2.5 bg-green-950/20 border border-green-800/30 rounded-lg">
          <p className="text-xs text-green-300 font-semibold">
            ✓ {workingModels.length} model(s) accessible on your key
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Click "Use This" next to any green model to select it automatically.
          </p>
        </div>
      )}

      {!testing && Object.keys(results).length > 0 && workingModels.length === 0 && (
        <div className="p-2.5 bg-red-950/20 border border-red-800/30 rounded-lg">
          <p className="text-xs text-red-300 font-semibold">
            ✗ No models accessible. Check your API key at console.anthropic.com.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModelTester;